import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { CalendarClock, Plug, SquarePen, type LucideIcon } from "lucide-react";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { cn } from "@/lib/utils";
import { useIsHydrated } from "./skeletonLayout";
import { readCachedAutomationsEnabled } from "./useCachedAutomationsEnabled";
import type { CodesmithSidebarVariant } from "./sidebarVariant";
import { REDESIGN_ROW_FILL_INSET_CLASS } from "./compactSessionRowStyles";

/** Primary navigation pinned above the session lists. */

const NAV_ROW = "group flex h-8 w-full items-center gap-2 rounded-md border border-transparent p-1.5 type-body-s-regular cursor-pointer transition-none text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 focus-visible:outline-none focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white";
const NAV_ROW_ACTIVE = "bg-[#0000000f] text-neutral-950 [&_svg]:text-neutral-900 dark:bg-white/6 dark:text-white dark:[&_svg]:text-white";
const REDESIGN_NAV_ROW = cn("group flex h-8 w-full items-center gap-1.5 rounded-[4px] px-2 type-body-s-regular cursor-pointer transition-none text-neutral-700 hover:bg-black/[0.04] hover:text-neutral-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--focus-border)] [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white", REDESIGN_ROW_FILL_INSET_CLASS);
function navClasses(variant: CodesmithSidebarVariant) {
  return {
    nav: variant === "redesign" ? "flex flex-col px-2 pb-4" : "flex flex-col gap-1 px-2 pt-app-header-gap pb-2",
    row: variant === "redesign" ? REDESIGN_NAV_ROW : NAV_ROW,
    icon: variant === "redesign" ? "size-4 shrink-0" : "size-[18px] shrink-0"
  };
}

// The shortcut keycaps stay hidden until the row is hovered or
// keyboard-focused, keeping the resting sidebar clean. Opacity (not display)
// preserves the row's layout so the label doesn't shift when they appear.
function ShortcutChip(t0) {
  const $ = _c(2);
  const {
    shortcut
  } = t0;
  let t1;
  if ($[0] !== shortcut) {
    t1 = <ShortcutKeys keys={Array.from(shortcut)} className="shrink-0 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100" />;
    $[0] = shortcut;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function RowContents(t0) {
  const $ = _c(11);
  const {
    icon: Icon,
    label,
    shortcut,
    iconClassName: t1
  } = t0;
  const iconClassName = t1 === undefined ? "size-[18px] shrink-0" : t1;
  let t2;
  if ($[0] !== Icon || $[1] !== iconClassName) {
    t2 = <Icon className={iconClassName} />;
    $[0] = Icon;
    $[1] = iconClassName;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== label) {
    t3 = <span className="flex-1 truncate text-left">{label}</span>;
    $[3] = label;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== shortcut) {
    t4 = shortcut ? <ShortcutChip shortcut={shortcut} /> : null;
    $[5] = shortcut;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <>{t2}{t3}{t4}</>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
export interface SidebarPrimaryNavProps {
  /** Starts a new Codesmith session (same action as the old toolbar new-chat). */
  onNewSession: () => void;
  /** Resolved automations route, or null/undefined to hide the row (flag off). */
  automationsHref?: string | null;
  onPrefetchAutomations?: () => void;
  /** Resolved MCP Connections route. */
  mcpConnectionsHref: string;
  /**
   * Called after a subpage row navigates (e.g. to collapse the sidebar on
   * mobile). The New session row deliberately does NOT call this: its mobile
   * collapse and write-blocked gating are owned by `onNewSession`, so invoking
   * `onNavigate` there too would double-fire the collapse (and on mobile toggle
   * the sidebar back open).
   */
  onNavigate?: () => void;
  newSessionShortcut?: string;
  automationsShortcut?: string;
  /** Highlights the row matching the current Codesmith subpage. */
  activeRow?: "new-session" | "automations" | "mcp-connections" | null;
  className?: string;
  variant?: CodesmithSidebarVariant;
}
export function SidebarPrimaryNav(t0) {
  const $ = _c(36);
  const {
    onNewSession,
    automationsHref,
    onPrefetchAutomations,
    mcpConnectionsHref,
    onNavigate,
    newSessionShortcut: t1,
    automationsShortcut: t2,
    activeRow: t3,
    className,
    variant: t4
  } = t0;
  const newSessionShortcut = t1 === undefined ? "\u2325\u21E7N" : t1;
  const automationsShortcut = t2 === undefined ? "\u2325\u21E7A" : t2;
  const activeRow = t3 === undefined ? null : t3;
  const variant = t4 === undefined ? "legacy" : t4;
  const router = useRouter();
  const org = router.query.org;
  const newSessionHref = typeof org === "string" ? `/${org}/sessions/new` : "/sessions/new";
  let T0;
  let classes;
  let t10;
  let t11;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== activeRow || $[1] !== automationsHref || $[2] !== automationsShortcut || $[3] !== className || $[4] !== mcpConnectionsHref || $[5] !== newSessionHref || $[6] !== newSessionShortcut || $[7] !== onNavigate || $[8] !== onNewSession || $[9] !== onPrefetchAutomations || $[10] !== variant) {
    classes = navClasses(variant);
    const NAV = classes.row;
    const redesign = variant === "redesign";
    t9 = cn(classes.nav, className);
    let t12;
    if ($[20] !== onNewSession) {
      t12 = e => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
          return;
        }
        e.preventDefault();
        onNewSession();
      };
      $[20] = onNewSession;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    t10 = <Link href={newSessionHref} onClick={t12} aria-keyshortcuts="Alt+Shift+N" aria-current={activeRow === "new-session" ? "page" : undefined} className={cn(NAV, activeRow === "new-session" && NAV_ROW_ACTIVE)}><RowContents icon={SquarePen} label={redesign ? "New Chat" : "New session"} shortcut={newSessionShortcut} iconClassName={classes.icon} /></Link>;
    t11 = automationsHref ? <Link href={automationsHref} onMouseEnter={onPrefetchAutomations} onFocus={onPrefetchAutomations} onClick={onNavigate} aria-keyshortcuts="Alt+Shift+A" aria-current={activeRow === "automations" ? "page" : undefined} className={cn(NAV, activeRow === "automations" && NAV_ROW_ACTIVE)}><RowContents icon={CalendarClock} label="Automations" shortcut={automationsShortcut} iconClassName={classes.icon} /></Link> : null;
    T0 = Link;
    t5 = mcpConnectionsHref;
    t6 = onNavigate;
    t7 = activeRow === "mcp-connections" ? "page" : undefined;
    t8 = cn(NAV, activeRow === "mcp-connections" && NAV_ROW_ACTIVE);
    $[0] = activeRow;
    $[1] = automationsHref;
    $[2] = automationsShortcut;
    $[3] = className;
    $[4] = mcpConnectionsHref;
    $[5] = newSessionHref;
    $[6] = newSessionShortcut;
    $[7] = onNavigate;
    $[8] = onNewSession;
    $[9] = onPrefetchAutomations;
    $[10] = variant;
    $[11] = T0;
    $[12] = classes;
    $[13] = t10;
    $[14] = t11;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    T0 = $[11];
    classes = $[12];
    t10 = $[13];
    t11 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t12;
  if ($[22] !== classes.icon) {
    t12 = <RowContents icon={Plug} label="MCP Connections" iconClassName={classes.icon} />;
    $[22] = classes.icon;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== T0 || $[25] !== t12 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8) {
    t13 = <T0 href={t5} onClick={t6} aria-current={t7} className={t8}>{t12}</T0>;
    $[24] = T0;
    $[25] = t12;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t13 || $[34] !== t9) {
    t14 = <nav className={t9}>{t10}{t11}{t13}</nav>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t13;
    $[34] = t9;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
}

/**
 * Non-interactive twin of SidebarPrimaryNav for the sidebar skeleton: these
 * rows are static chrome, so the skeleton renders them for real instead of
 * shimmer blocks and the top of the sidebar doesn't shift or pop when the
 * live sidebar mounts. The Automations row mirrors the live sidebar's
 * pre-flag fallback by reading the same per-org cache.
 */
export function SidebarPrimaryNavStatic(t0) {
  const $ = _c(28);
  const {
    className,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "legacy" : t1;
  const router = useRouter();
  const org = router.query.org;
  const isHydrated = useIsHydrated();
  let t2;
  if ($[0] !== isHydrated || $[1] !== org) {
    t2 = isHydrated && readCachedAutomationsEnabled(typeof org === "string" ? org : null);
    $[0] = isHydrated;
    $[1] = org;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const showAutomations = t2;
  let classes;
  let redesign;
  let t3;
  if ($[3] !== className || $[4] !== variant) {
    classes = navClasses(variant);
    redesign = variant === "redesign";
    t3 = cn(classes.nav, className);
    $[3] = className;
    $[4] = variant;
    $[5] = classes;
    $[6] = redesign;
    $[7] = t3;
  } else {
    classes = $[5];
    redesign = $[6];
    t3 = $[7];
  }
  const t4 = redesign ? "New Chat" : "New session";
  let t5;
  if ($[8] !== classes.icon || $[9] !== t4) {
    t5 = <RowContents icon={SquarePen} label={t4} iconClassName={classes.icon} />;
    $[8] = classes.icon;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== classes.row || $[12] !== t5) {
    t6 = <div className={classes.row}>{t5}</div>;
    $[11] = classes.row;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== classes.icon || $[15] !== classes.row || $[16] !== showAutomations) {
    t7 = showAutomations && <div className={classes.row}><RowContents icon={CalendarClock} label="Automations" iconClassName={classes.icon} /></div>;
    $[14] = classes.icon;
    $[15] = classes.row;
    $[16] = showAutomations;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== classes.icon) {
    t8 = <RowContents icon={Plug} label="MCP Connections" iconClassName={classes.icon} />;
    $[18] = classes.icon;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== classes.row || $[21] !== t8) {
    t9 = <div className={classes.row}>{t8}</div>;
    $[20] = classes.row;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t3 || $[24] !== t6 || $[25] !== t7 || $[26] !== t9) {
    t10 = <nav className={t3}>{t6}{t7}{t9}</nav>;
    $[23] = t3;
    $[24] = t6;
    $[25] = t7;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}